import type { PropDef, PropKind, TrackDef, Vec2 } from '../core/types';
import { hash01 } from '../core/math';
import { roundedLoop } from './track-geometry';

/**
 * „Hajnali beton" – külvárosi raktárnegyed.
 * Nagy, gyors kanyarok, hosszú egyenesek, szűk átkötő a raktárak között.
 */
const CENTERLINE: Vec2[] = roundedLoop([
  { x: 480, y: 1960, r: 260 },
  { x: 2560, y: 1960, r: 260 },
  { x: 2560, y: 1300, r: 240 },
  { x: 3000, y: 1300, r: 240 },
  { x: 3000, y: 600, r: 260 },
  { x: 480, y: 600, r: 260 },
]);

const props: PropDef[] = [];
const add = (kind: PropKind, s: number, lateral: number, scale = 1, variant = 0): void => {
  props.push({ kind, s, lateral, scale, variant });
};

// külső ív: raktárak, néha üres telek
for (let i = 0; i < 34; i++) {
  const s = (i + 0.35) / 34;
  const h = hash01(i * 92821 + 91);
  const h2 = hash01(i * 31337 + 23);
  if (h > 0.08) add('building', s, 262 + h2 * 110, 1.1 + h * 0.7, 2 + Math.floor(h * 4));
  if (h2 > 0.6) add('tree', s + 0.006, 214 + h * 30, 0.7 + h2 * 0.4, Math.floor(h * 3));
  if (h < 0.14) add('kiosk', s + 0.004, 200, 1, Math.floor(h2 * 2));
}

// belső ív: beton, konténerek helyett kúpok és bokrok
for (let i = 0; i < 52; i++) {
  const s = i / 52;
  const h = hash01(i * 7829 + 41);
  const h2 = hash01(i * 104729 + 7);
  if (h > 0.5) add('tree', s, -(178 + h * 40), 0.7 + h2 * 0.5, Math.floor(h2 * 3));
  if (h < 0.12) add('cone', s + 0.004, -150 - h2 * 20, 1, i % 2);
}

// lámpák
for (let i = 0; i < 36; i++) add('lamp', i / 36, i % 2 === 0 ? 186 : -186, 1, i % 3);

// ipari utcabútorok
for (let i = 0; i < 24; i++) {
  const s = (i + 0.2) / 24;
  const h = hash01(i * 5501 + 71);
  if (h < 0.34) add('hydrant', s, 174, 1, 0);
  else if (h < 0.62) add('bin', s, -174, 1, 0);
  else if (h < 0.86) add('sign', s, 190, 1, i % 4);
}

for (const s of [0.08, 0.28, 0.5, 0.72, 0.9]) add('crosswalk', s, 0, 1, 0);
for (const s of [0.14, 0.4, 0.66, 0.86]) {
  add('arrow', s, -48, 1, 0);
  add('arrow', s + 0.012, 48, 1, 1);
}

// tócsák, parkoló autók, kúpos terelés
add('puddle', 0.33, 24, 1.6, 0);
add('puddle', 0.35, -60, 1.2, 0);
add('parkedCar', 0.57, 200, 1, 3);
add('parkedCar', 0.585, 200, 1, 1);
for (let i = 0; i < 9; i++) add('cone', 0.47 + i * 0.004, -70 + i * 8, 1, i % 2);

export const SUBURB_TRACK: TrackDef = {
  id: 'hajnali-beton',
  name: 'Hajnali beton',
  subtitle: 'Külvárosi raktárnegyed · 3 kör',
  description:
    'Hosszú egyenesek a raktárak között, egy szűk átkötő a betonkerítés mellett, majd egy nagy, gyors jobbos. Itt a végsebesség és a türelem dönt.',
  centerline: CENTERLINE,
  roadHalfWidth: 104,
  kerbWidth: 12,
  sidewalkWidth: 30,
  startS: 0.04,
  laps: 3,
  theme: 'suburb',
  props,
  boostPads: [
    { s: 0.2, lateral: 0, length: 250, width: 150 },
    { s: 0.56, lateral: 0, length: 250, width: 150 },
    { s: 0.78, lateral: 0, length: 250, width: 150 },
  ],
  itemRows: [
    { s: 0.1, lateral: 0, count: 4, spacing: 54 },
    { s: 0.34, lateral: 0, count: 4, spacing: 54 },
    { s: 0.63, lateral: 0, count: 4, spacing: 54 },
    { s: 0.86, lateral: 0, count: 4, spacing: 54 },
  ],
  ambient: {
    sky: '#1d222b',
    asphalt: '#43474e',
    asphaltDark: '#363a41',
    lane: '#d9c98f',
    kerb: '#b9503c',
    sidewalk: '#8d8b85',
    wall: '#252a33',
    accent: '#ffb347',
    shadow: 'rgba(8,10,14,0.42)',
  },
  cameraZoom: 0.96,
};
